import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceStatus } from "@/models/enums/service";
import Service from "@/services";
import { mergeObjects } from "@/utils/utils";

const service = new Service.ManagementService();

// Interface
interface ManagementTrackingState {
  summary: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: any;
    legend?: any;
  };
  info: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data?: any;
  };
  brandItems: {
    data: Array<{ id: number; name: string }>;
    loading: "idle" | "pending" | "succeeded" | "failed";
    error: string | null;
    offset: number;
    total: number;
  };
  skuItems: {
    data: Array<{ id: number; name: string; code: string }>;
    loading: "idle" | "pending" | "succeeded" | "failed";
    error: string | null;
    offset: number;
    total: number;
  };
  dailyTrackings: {
    data: Array<{
      date: string;
      skus: Array<{
        code: string;
        name: string;
        target: string;
        totalOrdering: string;
        totalOrderingBPlus: string;
        totalTargetDiff: string;
      }>;
    }>;
    loading: "idle" | "pending" | "succeeded" | "failed";
    error: string | null;
  };
}

// Initialize State
const initialState: ManagementTrackingState = {
  summary: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
    legend: undefined,
  },
  info: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: undefined,
  },
  brandItems: {
    data: [],
    loading: "idle",
    error: null,
    offset: 0,
    total: 0,
  },
  skuItems: {
    data: [],
    loading: "idle",
    error: null,
    offset: 0,
    total: 0,
  },
  dailyTrackings: {
    data: [],
    loading: "idle",
    error: null,
  },
};

// Reducer
export const slice = createSlice({
  name: "ManagementDetail",
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => mergeObjects({ ...state }, action.payload),
  },
  extraReducers: (builder) => {
    // Fetch User
    builder.addCase(onFetchTrackingSummary.pending, (state) => {
      state.summary.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchTrackingSummary.fulfilled, (state, action) => {
      state.summary.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.summary.data = action.payload.data;
      }
    });
    builder.addCase(onFetchTrackingSummary.rejected, (state, action) => {
      state.summary.network.code = ServiceStatus.failed;
      state.summary.network.error = (action.payload as string) || "Failed to fetch data";
    });
    builder.addCase(onFetchTrackingInfo.pending, (state) => {
      state.info.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchTrackingInfo.fulfilled, (state, action) => {
      state.info.network.code = ServiceStatus.succeeded;
      if (action.payload != null) {
        state.info.data = action.payload.data;
      }
    });
    builder.addCase(onFetchTrackingInfo.rejected, (state, action) => {
      state.info.network.code = ServiceStatus.failed;
      state.info.network.error = (action.payload as string) || "Failed to fetch data";
    });

    // Fetch Daily Trackings
    builder
      .addCase(onFetchTrackingDaily.pending, (state) => {
        state.dailyTrackings.loading = "pending";
      })
      .addCase(onFetchTrackingDaily.fulfilled, (state, action) => {
        state.dailyTrackings.loading = "succeeded";
        state.dailyTrackings.data = action.payload.data.trackings;
        state.dailyTrackings.error = null;
      })
      .addCase(onFetchTrackingDaily.rejected, (state, action) => {
        state.dailyTrackings.loading = "failed";
        state.dailyTrackings.error = action.payload as string;
      });

    // Brand items reducers
    builder
      .addCase(onFetchTrackingBrand.pending, (state) => {
        state.brandItems.loading = "pending";
      })
      .addCase(onFetchTrackingBrand.fulfilled, (state, action) => {
        const { data } = action.payload;
        const isInitialLoad = action.meta.arg.offset === 0;

        state.brandItems.loading = "succeeded";
        state.brandItems.data = isInitialLoad ? data : [...state.brandItems.data, ...data];
        state.brandItems.offset = isInitialLoad ? data.length : state.brandItems.offset + data.length;
        state.brandItems.total = action.payload.xTotalCount || state.brandItems.total;
      })
      .addCase(onFetchTrackingBrand.rejected, (state, action) => {
        state.brandItems.loading = "failed";
        state.brandItems.error = action.payload as string;
      });

    // SKU items reducers
    builder
      .addCase(onFetchTrackingSku.pending, (state) => {
        state.skuItems.loading = "pending";
      })
      .addCase(onFetchTrackingSku.fulfilled, (state, action) => {
        const { data } = action.payload;
        const isInitialLoad = action.meta.arg.offset === 0;

        state.skuItems.loading = "succeeded";
        state.skuItems.data = isInitialLoad ? data : [...state.skuItems.data, ...data];
        state.skuItems.offset = isInitialLoad ? data.length : state.skuItems.offset + data.length;
        state.skuItems.total = action.payload.xTotalCount || state.skuItems.total;
      })
      .addCase(onFetchTrackingSku.rejected, (state, action) => {
        state.skuItems.loading = "failed";
        state.skuItems.error = action.payload as string;
      });
  },
});

export const onFetchTrackingSummary = createAsyncThunk("management/tracking/summary", async (params: { customerId: string; brands?: string }, thunkAPI) => {
  try {
    const apiData = await service.fetchTrackingSummary(params);
    return apiData;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
export const onFetchTrackingInfo = createAsyncThunk("management/tracking/info", async (params: { brands?: string; customerId: string }, thunkAPI) => {
  try {
    const apiData = await service.fetchTrackingInfo(params);
    return apiData;
  } catch (error: any) {
    // Handle any errors and return an error action
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

export const onFetchTrackingDaily = createAsyncThunk("management/tracking/daily", async (params: { customerId: string; brandIds?: number[]; skus?: string[] }, thunkAPI) => {
  try {
    const apiData = await service.fetchTrackingDaily(params);
    return apiData;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response?.data?.message || "Failed to fetch daily tracking");
  }
});

export const onFetchTrackingBrand = createAsyncThunk(
  "management/tracking/brand",
  async (
    params: {
      customerid: string;
      searchText?: string;
      limit?: number;
      offset?: number;
    },
    thunkAPI
  ) => {
    try {
      const apiData = await service.fetchTrackingBrand(params);
      return apiData;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || "Failed to fetch brands");
    }
  }
);

export const onFetchTrackingSku = createAsyncThunk(
  "management/tracking/sku",
  async (
    params: {
      customerid: string;
      brandIds?: number[];
      searchText?: string;
      limit?: number;
      offset?: number;
    },
    thunkAPI
  ) => {
    try {
      const apiData = await service.fetchTrackingSku(params);
      return apiData;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || "Failed to fetch SKUs");
    }
  }
);

// Actions
export const { patch, reset } = slice.actions;

// Export
export default slice.reducer;
